import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tagsApi, transactionsApi } from '@/services/api';
import { formatCurrency, CATEGORY_MAP, type TransactionTag } from '@tally/shared';
import { Search, Download, ExternalLink, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare, Square, MinusSquare, Merge, X, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/utils/cn';
import { ROUTES } from '@/routes';
import { TransactionDetailPanel } from '@/components/shared/TransactionDetailPanel';
import { TagChip } from '@/components/tags/TagChip';
import { TagPicker } from '@/components/tags/TagPicker';
import { invalidateExpenseQueries, tripLabel, useTags, useTripOptions } from '@/components/tags/tagQueries';

type SortField = 'transaction_date' | 'amount' | 'merchant_name' | 'category';
type SortOrder = 'asc' | 'desc';

/** Filters a link can preset, e.g. /app/transactions?tagId=… from Subscriptions or Insights. */
const URL_FILTERS = ['tagId', 'tripId', 'category', 'dateFrom', 'dateTo', 'view'];

function filtersFrom(params: URLSearchParams): Record<string, string> {
  const filters: Record<string, string> = {};
  for (const key of URL_FILTERS) {
    const value = params.get(key);
    if (value) filters[key] = value;
  }
  return filters;
}

export function TransactionsPage() {
  const queryClient = useQueryClient();
  const [urlParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>(() => filtersFrom(urlParams));
  const [searchQuery, setSearchQuery] = useState(() => urlParams.get('searchQuery') ?? '');
  // A new link to this page (same route, different query) replaces the filters.
  const urlKey = urlParams.toString();
  useEffect(() => {
    setFilters(filtersFrom(new URLSearchParams(urlKey)));
    setSearchQuery(new URLSearchParams(urlKey).get('searchQuery') ?? '');
    setPage(1);
  }, [urlKey]);
  const [sortBy, setSortBy] = useState<SortField>('transaction_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedTx, setSelectedTx] = useState<any | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => transactionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transactions-summary'] });
      setSelectedTx(null);
    },
  });

  const mergeMutation = useMutation({
    mutationFn: (transactionIds: string[]) => transactionsApi.merge(transactionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transactions-summary'] });
      setSelectedIds(new Set());
      setSelectMode(false);
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (body: { tripId?: string | null; addTagIds?: string[]; removeTagIds?: string[]; message: string }) =>
      transactionsApi.bulk({ transactionIds: [...selectedIds], tripId: body.tripId, addTagIds: body.addTagIds, removeTagIds: body.removeTagIds }),
    onSuccess: (_res, body) => {
      invalidateExpenseQueries(queryClient);
      setBulkMessage(body.message);
    },
    onError: (err) => setBulkMessage(err instanceof Error ? err.message : 'Could not update the selection'),
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkMessage(null);
  };

  const params: Record<string, string> = { page: String(page), pageSize: '30', sortBy, sortOrder, ...filters };
  if (searchQuery) params.searchQuery = searchQuery;

  const { data, isLoading } = useQuery({
    queryKey: ['transactions', params],
    queryFn: () => transactionsApi.list(params),
  });

  const { data: summaryData } = useQuery({
    queryKey: ['transactions-summary', filters],
    queryFn: () => transactionsApi.summary(filters),
  });

  const trips = useTripOptions();
  const tags = useTags();

  const transactions = data?.data?.items || [];
  const pageIds: string[] = transactions.map((tx: any) => tx.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someOnPageSelected = pageIds.some((id) => selectedIds.has(id));
  const toggleSelectPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) {
        if (allOnPageSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };
  // Tags on the selected rows of this page, offered for removal.
  const selectedTags = new Map<string, TransactionTag>();
  for (const tx of transactions as any[]) {
    if (selectedIds.has(tx.id)) for (const t of (tx.tags ?? []) as TransactionTag[]) selectedTags.set(t.id, t);
  }
  const selectedKey = [...selectedIds].sort().join(',');
  const { data: bulkSuggestions } = useQuery({
    queryKey: ['tag-suggestions', 'bulk', selectedKey],
    queryFn: () => tagsApi.suggestions([...selectedIds]),
    enabled: selectMode && selectedIds.size > 0 && selectedIds.size <= 200,
  });
  const count = selectedIds.size;
  const noun = `${count} expense${count === 1 ? '' : 's'}`;
  const total = data?.data?.total || 0;
  const hasMore = data?.data?.hasMore || false;
  const summary = summaryData?.data;

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Expenses</h1>
          <p className="text-sm text-muted-foreground">
            {total} expense{total === 1 ? '' : 's'} {filters.view === 'not_spending' ? 'hidden as not spending' : summary ? `totaling ${formatCurrency(summary.totalSpent)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
            className={cn(
              'flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium',
              selectMode ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-accent'
            )}
          >
            {selectMode ? <><X className="h-4 w-4" /> Cancel</> : <><CheckSquare className="h-4 w-4" /> Select</>}
          </button>
          <a
            href={transactionsApi.export({ format: 'csv', ...filters })}
            className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            <Download className="h-4 w-4" /> Export
          </a>
        </div>
      </div>

      {selectMode && count >= 1 && (
        <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">{noun} selected</span>
            <select
              value=""
              disabled={bulkMutation.isPending}
              onChange={(e) => {
                if (!e.target.value) return;
                const tripId = e.target.value === 'none' ? null : e.target.value;
                const trip = trips.find((t) => t.id === tripId);
                bulkMutation.mutate({ tripId, message: tripId ? `Moved ${noun} to ${trip?.name ?? 'the trip'}` : `Took ${noun} off their trips` });
              }}
              className="max-w-[220px] rounded-lg border border-input bg-background px-3 py-1.5 text-sm disabled:opacity-50"
              aria-label="Move selected to trip"
            >
              <option value="">Move to trip…</option>
              <option value="none">No trip</option>
              {trips.map((t) => <option key={t.id} value={t.id}>{tripLabel(t)}</option>)}
            </select>
            <TagPicker
              tags={tags}
              placeholder="Add tag to selected…"
              suggestions={bulkSuggestions?.data}
              disabled={bulkMutation.isPending}
              onPick={(tag) => bulkMutation.mutate({ addTagIds: [tag.id], message: `Tagged ${noun} “${tag.name}”` })}
              className="w-56"
            />
            {selectedTags.size > 0 && (
              <select
                value=""
                disabled={bulkMutation.isPending}
                onChange={(e) => {
                  const tag = selectedTags.get(e.target.value);
                  if (tag) bulkMutation.mutate({ removeTagIds: [tag.id], message: `Removed “${tag.name}” from ${noun}` });
                }}
                className="max-w-[200px] rounded-lg border border-input bg-background px-3 py-1.5 text-sm disabled:opacity-50"
                aria-label="Remove tag from selected"
              >
                <option value="">Remove tag…</option>
                {[...selectedTags.values()].map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
            {count >= 2 && (
              <button
                onClick={() => mergeMutation.mutate([...selectedIds])}
                disabled={mergeMutation.isPending}
                className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
                title="Combine duplicates of the same charge into one"
              >
                <Merge className="h-4 w-4" /> {mergeMutation.isPending ? 'Merging...' : 'Merge'}
              </button>
            )}
            <button onClick={() => { setSelectedIds(new Set()); setBulkMessage(null); }} className="ml-auto text-xs text-muted-foreground hover:text-foreground">
              Clear selection
            </button>
          </div>
          {bulkMessage && <p className="text-xs text-muted-foreground" role="status">{bulkMessage}</p>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search merchants, descriptions..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium md:hidden"
          aria-expanded={showFilters}
        >
          <SlidersHorizontal className="h-4 w-4" /> Filters{Object.values(filters).filter(Boolean).length > 0 ? ` (${Object.values(filters).filter(Boolean).length})` : ''}
        </button>
        <div className={cn('w-full flex-wrap items-center gap-3 md:flex md:w-auto', showFilters ? 'flex' : 'hidden')}>
          <select
            value={filters.category || ''}
            onChange={(e) => { setFilters({ ...filters, category: e.target.value || undefined } as any); setPage(1); }}
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">All categories</option>
            {[...CATEGORY_MAP.entries()].map(([id, cat]) => (
              <option key={id} value={id}>{cat.label}</option>
            ))}
          </select>

          <select
            value={filters.tripId || ''}
            onChange={(e) => { setFilters({ ...filters, tripId: e.target.value || undefined } as any); setPage(1); }}
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm max-w-[220px]"
          >
            <option value="">All trips</option>
            <option value="none">No trip</option>
            {trips.map((t) => (
              <option key={t.id} value={t.id}>{tripLabel(t)}</option>
            ))}
          </select>

          <select
            value={filters.view || ''}
            onChange={(e) => { setFilters({ ...filters, view: e.target.value || undefined } as any); setPage(1); }}
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
            aria-label="Show"
          >
            <option value="">Spending</option>
            <option value="not_spending">Not spending (hidden)</option>
          </select>

          <select
            value={filters.tagId || ''}
            onChange={(e) => { setFilters({ ...filters, tagId: e.target.value || undefined } as any); setPage(1); }}
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm max-w-[200px]"
            aria-label="Filter by tag"
          >
            <option value="">All tags</option>
            {tags.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.transactionCount})</option>
            ))}
          </select>

          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => { setFilters({ ...filters, dateFrom: e.target.value || undefined } as any); setPage(1); }}
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
            placeholder="From"
          />
          <span className="text-muted-foreground text-sm">to</span>
          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => { setFilters({ ...filters, dateTo: e.target.value || undefined } as any); setPage(1); }}
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
            placeholder="To"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : (
          <>
          {/* Phones: one card per expense. */}
          <ul className="divide-y divide-border md:hidden">
            {transactions.map((tx: any) => (
              <li key={tx.id}>
                <button
                  onClick={() => selectMode ? toggleSelect(tx.id) : setSelectedTx(tx)}
                  className={cn('flex w-full items-start gap-3 px-4 py-3 text-left', selectedIds.has(tx.id) && 'bg-primary/10')}
                >
                  {selectMode && (selectedIds.has(tx.id) ? <CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> : <Square className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />)}
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate font-medium">{tx.merchantName}</span>
                      <span className="shrink-0 font-semibold">{formatCurrency(tx.amount, tx.currency)}</span>
                    </span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: CATEGORY_MAP.get(tx.category)?.color }} />
                      <span className="truncate">{tx.date?.split('T')[0]} · {CATEGORY_MAP.get(tx.category)?.label || tx.category}{tx.tripName ? ` · ${tx.tripName}` : ''}</span>
                    </span>
                    {(tx.tags?.length ?? 0) > 0 && (
                      <span className="mt-1.5 flex flex-wrap gap-1">
                        {(tx.tags as TransactionTag[]).slice(0, 3).map((t) => <TagChip key={t.id} name={t.name} color={t.color} source={t.source} />)}
                        {tx.tags.length > 3 && <span className="text-xs text-muted-foreground">+{tx.tags.length - 3}</span>}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
            {transactions.length === 0 && <li className="px-4 py-10 text-center text-sm text-muted-foreground">No expenses match.</li>}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr className="text-left text-xs text-muted-foreground">
                  {selectMode && (
                    <th className="px-3 py-3 w-10">
                      <button onClick={toggleSelectPage} aria-label={allOnPageSelected ? 'Unselect this page' : 'Select this page'}>
                        {allOnPageSelected ? <CheckSquare className="h-4 w-4 text-primary" /> : someOnPageSelected ? <MinusSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4 text-muted-foreground" />}
                      </button>
                    </th>
                  )}
                  <th className="px-4 py-3 font-medium">
                    <button onClick={() => handleSort('transaction_date')} className="flex items-center gap-1.5 hover:text-foreground">
                      Date <SortIcon field="transaction_date" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button onClick={() => handleSort('merchant_name')} className="flex items-center gap-1.5 hover:text-foreground">
                      Merchant <SortIcon field="merchant_name" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button onClick={() => handleSort('category')} className="flex items-center gap-1.5 hover:text-foreground">
                      Category <SortIcon field="category" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">Trip</th>
                  <th className="px-4 py-3 font-medium">Tags</th>
                  <th className="px-4 py-3 font-medium">Sources</th>
                  <th className="px-4 py-3 font-medium">
                    <button onClick={() => handleSort('amount')} className="flex items-center gap-1.5 hover:text-foreground ml-auto">
                      Amount <SortIcon field="amount" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {transactions.map((tx: any) => (
                  <tr
                    key={tx.id}
                    onClick={() => selectMode ? toggleSelect(tx.id) : setSelectedTx(tx)}
                    className={cn(
                      'transition-colors cursor-pointer',
                      selectedIds.has(tx.id) ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-accent/30'
                    )}
                  >
                    {selectMode && (
                      <td className="px-3 py-3">
                        <button onClick={(e) => { e.stopPropagation(); toggleSelect(tx.id); }}>
                          {selectedIds.has(tx.id) ? (
                            <CheckSquare className="h-4 w-4 text-primary" />
                          ) : (
                            <Square className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{tx.date?.split('T')[0]}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{tx.merchantName}</p>
                        {tx.description && tx.description !== tx.merchantName && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">{tx.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2 py-0.5 text-xs">
                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: CATEGORY_MAP.get(tx.category)?.color }} />
                        {CATEGORY_MAP.get(tx.category)?.label || tx.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {tx.tripId ? (
                        <a
                          href={ROUTES.app.trip(tx.tripId)}
                          onClick={(e) => e.stopPropagation()}
                          className="block max-w-[180px] truncate text-xs text-primary hover:underline"
                        >
                          {tx.tripName || 'View trip'}
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex max-w-[260px] flex-wrap gap-1">
                        {((tx.tags ?? []) as TransactionTag[]).slice(0, 3).map((t) => (
                          <TagChip key={t.id} name={t.name} color={t.color} source={t.source} />
                        ))}
                        {(tx.tags?.length ?? 0) > 3 && <span className="text-xs text-muted-foreground">+{tx.tags.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        {tx.sources?.map((s: any, i: number) => (
                          s.deepLink ? (
                            <a key={i} href={s.deepLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary hover:bg-primary/20">
                              {s.source} <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          ) : (
                            <span key={i} className="rounded bg-muted px-1.5 py-0.5 text-xs">{s.source}</span>
                          )
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{formatCurrency(tx.amount, tx.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <p className="text-xs text-muted-foreground">
            Showing {Math.min((page - 1) * 30 + 1, total)}–{Math.min(page * 30, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg p-1.5 hover:bg-accent disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium">Page {page}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className="rounded-lg p-1.5 hover:bg-accent disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      {selectedTx && (
        <TransactionDetailPanel
          transaction={transactions.find((tx: any) => tx.id === selectedTx.id) ?? selectedTx}
          onClose={() => setSelectedTx(null)}
          onDelete={(id) => deleteMutation.mutate(id)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
